import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import SearchBar from '../../components/SearchBar';
import FlatList from '../../components/FlatList';
import DukaanCard from '../../components/DukaanCard';
import ProductItem from '@/components/DesignSystem/ProductItem';
import { Button } from '@/components/DesignSystem/AtomicComponents';
import {
  saveSearchedProductItems,
  saveStoreInfo,
  searchProductItems,
  setCart,
} from 'src/redux/actions';
import VariantModal from '../../components/VariantModal';
import VariantChooseRepeatModal from '../../components/VariantChooseRepeatModal';
import VariantSelectedListModal from '../../components/VariantSelectedListModal';

import { debounce } from '@/utils/debounce';
import { ITEM_ACTIVE_STATUS } from '@/utils/constants';
import { getUrlParams } from '@/utils/getUrlParams';
import { IS_SERVER } from '@/utils/checkRenderEnv';
import { ProductCustomizationPopUp } from '../../components/ProductCustomizationPopUp';
import { FullPageLoader } from '../../components/FullPageLoader/FullPageLoader';
import { useSSRSelector } from 'src/redux';
// import useSetCart from '@/utils/customHooks';

const SEARCH_PARAM = 'search_str';

const Search = (props) => {
  const storeInfo = useSSRSelector((state) => state.storeReducer.store);
  const router = useRouter();
  // const { triggerSetCart } = useSetCart();
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch();
  const { searchedProducts, personalizedProduct } = useSelector(
    (state) => state.catalogReducer
  );
  const cart = useSelector((state) => state.cartReducer);
  // const { setCartLoader } = useSelector(state => state.commonReducer);
  const loader = useRef(false);

  const debounceOnChange = useCallback(debounce(handleSearchChange, 500), []);

  function handleSearchChange(change) {
    const text = change.trim();
    if (text && text.length >= 3) {
      dispatch(saveSearchedProductItems({ items: [], isNext: 0, page: 1 }));
      dispatch(
        searchProductItems({
          store_id: storeInfo.store_id,
          search_text: text,
          page: 1,
        })
      );
    } else if (text && text.length === 0) {
      dispatch(saveSearchedProductItems({ items: [], isNext: 0, page: 1 }));
    }
  }

  const loadMoreInit = () => {
    if (!loader.current && searchedProducts.isNext) {
      loader.current = true;
      dispatch(
        searchProductItems(
          {
            store_id: storeInfo.store_id,
            search_text: searchText,
            page: searchedProducts.page + 1,
          },
          () => {
            loader.current = false;
          }
        )
      );
    }
  };

  const appendQueryParam = (searchStr) => {
    if (IS_SERVER) {
      return;
    }
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set(SEARCH_PARAM, searchStr);
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    // Update the browser's URL without reloading the page
    window.history.replaceState({}, '', newUrl);
  };

  const onSearchChangeHandle = (searchText) => {
    const limitedString = searchText.substr(0, 255);
    setSearchText(limitedString);
    debounceOnChange(limitedString);
    appendQueryParam(limitedString);
  };

  useEffect(() => {
    /** Save store info **/
    if (props.storeInfo) {
      dispatch(saveStoreInfo(props.storeInfo));
    }
    /** check if we have a search string */
    const { search_str } = getUrlParams([SEARCH_PARAM]);
    search_str && onSearchChangeHandle(search_str);
  }, []);

  useEffect(() => {
    if (cart.items && cart.items.length > 0) {
      setCartAsyncCall();
    }
  }, [cart.pay_amount]);

  const setCartAsyncCall = () => {
    return new Promise((resolve, reject) => {
      dispatch(
        setCart(
          {
            ...cart,
            order_type: cart.order_type,
            store_id: props?.storeInfo?.store_id,
            user_phone: cart.phone,
            cart_id: cart.cart_id,
          },
          (status) => {
            resolve();
          }
        )
      );
    });
  };

  const renderFilteredSearch = () => {
    return (
      <FlatList
        isLoaderVisible={false}
        LoaderComponent={DukaanCard}
        loaderProps={{ isLoader: true }}
        isNext={searchedProducts.isNext ? true : false}
        onScrollEnd={() => loadMoreInit()}
        renderList={() => {
          return (
            <div
              className="search-content__box"
              style={{
                width: '100%',
                maxWidth: 800,
                marginRight: 'auto',
                marginLeft: 'auto',
                transform: 'none',
              }}
            >
              {activeSearchedProductsItems().map((item, idx) => (
                <ProductItem
                  key={`search-item-${idx}`}
                  data={item}
                  isCartItem={false}
                  isCardType={false}
                />
              ))}
            </div>
          );
        }}
      ></FlatList>
    );
  };

  function activeSearchedProductsItems() {
    return searchedProducts?.items?.length
      ? searchedProducts?.items.filter(
          (item) => item?.is_active === ITEM_ACTIVE_STATUS.ACTIVE
        )
      : [];
  }

  const renderNotFound = () => {
    return (
      <div
        className="search-content__notfound"
        style={{
          display: 'flex',
        }}
      >
        <span>Sorry we couldn't find the item you searched</span>
      </div>
    );
  };

  return (
    searchedProducts.items &&
    Object.values(searchedProducts.items).length >= 0 && (
      <div className="search-page">
        <FullPageLoader />
        <article className="search-header">
          <span onClick={() => router.back()}>
            <img
              loading="lazy"
              src="https://cdn.dotpe.in/longtail/themes/8073865/1cy3Zqf6.webp"
              alt="back"
            />
          </span>{' '}
          <span className="ml10">Search</span>
        </article>
        <section className="search-bar premium-search-bar">
          <SearchBar
            searchText={searchText}
            showClearIcon={searchText ? true : false}
            placeholder="Search for items..."
            onSearchChangeHandle={onSearchChangeHandle}
          />
        </section>
        {searchText.length > 0 ? (
          searchedProducts?.items?.length > 0 ? (
            renderFilteredSearch()
          ) : (
            renderNotFound()
          )
        ) : (
          <></>
        )}
        <main
          style={{
            display: 'flex',
            justifyContent: 'center',
            position: 'fixed',
            bottom: 0,
            width: '100%',
            backgroundColor: '#ffffff',
            padding: '10px 16px',
            zIndex: 99,
            borderTop: '1px solid #e2e2e2',
          }}
        >
          <Button
            maxWidth="400px"
            fluid={true}
            fontSize={18}
            py="10px"
            variant="outline"
            label="BACK"
            roundness={storeInfo?.theme?.roundness}
            textTransfomr="uppercase"
            backgroundColor={storeInfo?.theme?.colors?.primary_color}
            onClick={() => router.back()}
          />
        </main>
        {personalizedProduct?.personalizationPopUp && <ProductCustomizationPopUp />}
        <VariantModal />
        <VariantChooseRepeatModal />
        <VariantSelectedListModal />
      </div>
    )
  );
};

export default Search;
